import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import { Form, Input, Button, Row, Divider, Typography, Space, Popconfirm, Table, DatePicker, Col, Tooltip, Spin, Modal, message, Image } from "antd";
import DiagnosticSection from "./DiagnosticSection";
import { CheckCircleOutlined, CloseOutlined, DeleteOutlined, MessageOutlined, PlusOutlined, QuestionCircleOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import CommentForm from "./CommentForm";
import BaseSelect from "@/components/Elements/BaseSelect";
import { useDispatch, useSelector } from "react-redux";
import { isValidDate, isValidDateForm, orthodonticPlanTypes } from "@/utils/helpers";
import moment from "moment";
import { getByCustomer, updateOrthodonticPlan, approveOrthodonticPlan, unapproveOrthodonticPlan } from "@/redux/actions/orthodonticPlanActions";
import { checkPermission } from "@/utils/permission";
import PrintButton from "@/components/PrintButton/PrintButton";
import PrintOrthoPlan from "./PrintOrthoPlan";

const { Title, Text } = Typography;
const { TextArea } = Input;


const Index = (props) => {

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const query = useMemo(() => {
        return qs.parse(location.search.slice(1));
    }, [location.search]);
    let q = { ...query };

    const [form] = Form.useForm();
    const { customer } = props;

    const defaultType = 'type1';

    const allowUpdate = checkPermission('orthodontic_plans', 'update');
    const allowApproved = checkPermission('orthodontic_plans', 'approve');
    const allowUnapproved = checkPermission('orthodontic_plans', 'unapprove');

    const [showComment, setShowComment] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [type, setType] = useState(q.plan_type || defaultType);
    const [loading, setLoading] = useState(false);
    const doctors = useSelector((state) => state.config.doctors) || [];
    const [planData, setPlanData] = useState(null);
    const [toothData, setToothData] = useState([]);

    const defaultValues = {
        treatment_goals: new Array(3).fill({ issue: "", note: "" }),
        treatment_stages: [{}, {}],
        progress: [{}, {}],
        plan_doctor_ids: [],
        plan_creator_ids: [],
        muc_do_kho_ca: null,
    };

    const handleToothDataChange = useCallback((data) => {
        setToothData(data);
    }, []);

    const loadOrthodonticPlan = useCallback(async (type) => {
        if (!customer?.id) return;
        setLoading(true);
        try {
            const result = await dispatch(getByCustomer({ plan_type: type }, customer.id));
            const data = result?.data;   // hoặc tùy vào action trả về

            if (data && Object.keys(data).length > 0) {
                const normalized = {
                    ...data,
                    progress: data.progress?.map((item) => ({
                        ...item,
                        ngay_tk: isValidDateForm(item?.ngay_tk, false),
                    })) || [],
                };
                setPlanData(normalized);

                form.resetFields();
                form.setFieldsValue(normalized);
                setToothData(data.tooths || []);
                setIsApproved(data.is_approved || false);
            } else {
                // reset toàn bộ nếu không có dữ liệu
                setPlanData(defaultValues);
                form.resetFields();
                form.setFieldsValue(defaultValues);
                setToothData([]);
                setIsApproved(false);
            }
        } catch (error) {
            console.error("Failed to load orthodontic plan:", error);
            setPlanData(null);
            form.resetFields();
            form.setFieldsValue(defaultValues);
            setToothData([]);
            setIsApproved(false);
        } finally {
            setLoading(false);
        }
    }, [dispatch, customer?.id]);

    const onFinish = useCallback(
        async (values) => {
            if (!customer?.id) return;
            setLoading(true);
            try {
                const formatted = {
                    ...values,
                    tooths: toothData,
                    plan_type: type,
                    progress:
                        values.progress?.map((item) => ({
                            ...item,
                            ngay_tk: item.ngay_tk ? moment(item.ngay_tk).format("YYYY-MM-DD") : null,
                        })) || [],
                };

                await dispatch(updateOrthodonticPlan(customer.id, formatted));
                loadOrthodonticPlan(type);
            } finally {
                setLoading(false);
            }
        },
        [dispatch, customer?.id, toothData, type]
    );

    const onFinishFailed = (err) => {
        message.error('Vui lòng điền đầy đủ các trường bắt buộc');
    };

    useEffect(() => {
        // Nếu không có q.plan_type thì set mặc định và cập nhật URL
        if (!q.plan_type) {
            setType(defaultType);
            history.replace({
                pathname: location.pathname,
                search: qs.stringify({ ...query, plan_type: defaultType }),
            });
            loadOrthodonticPlan(defaultType);
            return;
        }

        // Nếu có plan_type thì load đúng type
        setType(q.plan_type || planData?.plan_type || defaultType);
        loadOrthodonticPlan(q.plan_type || defaultType);
    }, [q.plan_type, location.pathname]);

    const handleChangeType = (value) => {
        history.push({
            pathname: location.pathname,
            search: qs.stringify({ ...query, plan_type: value }),
        });
        setType(value);
        form.resetFields();
    };

    const tooltipTitle = [
        "1. Phân loại ca: Ca sai khớp cắn hạng 2 trên nền xương hạng 2 góc mở - Người trưởng thành/ Trẻ đang tăng trưởng",
        "2. Định hướng điều trị: Ca nhổ răng hay không nhổ răng tiền cối",
        "3. Chỉ định nhổ răng: Ghi rõ răng cần nhổ và thời điểm chỉ định nhổ (trước/ trong hoặc sau chỉnh nha) (cả răng tiền cối, răng khôn và các răng khác nếu có)",
        "4. Gói chỉnh nha: Ví dụ Gói mắc cài/ INV loại… / số…_ Level ca?",
        "5. Khí cụ hỗ trợ: Ví dụ Dự trù 2-4 mini vis/ Khí cụ nong hàm ….",
        "6. Thủ thuật hỗ trợ và yêu cầu hợp tác: Ví dụ: mài kẽ/ mang thun liên hàm",
        "7. Thời gian điều trị dự kiến: Ví dụ 24-30 tháng",
        "8. Duy trì sau hoàn tất chỉnh nha: Loại khí cụ chỉ định + thời gian mang khí cụ khuyến cáo + lịch tái khám định kỳ khuyến cáo > sau khi phê duyệt xong là phải khóa lại",
        "9. Ghi chú: Các vấn đề quan trọng khác cần ghi chú thêm. Ví dụ: XHD tăng trưởng muộn/ không thuận lợi ở ca hạng 3 xương có thể làm thay đổi profile và khớp cắn không mong muốn. Khi có vấn đề xảy ra, sẽ cần khám và đánh giá lại để có giải pháp phù hợp. (Các vấn đề/ mục tiêu và phác đồ điều trị mới phát sinh thì không nằm trong gói điều trị này)"
    ].join("\n");


    const handleApprove = async () => {
        if (!planData?.id) {
            message.error("Chưa có phác đồ chỉnh nha nào để xác nhận");
            return;
        }

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn approve phác đồ chỉnh nha?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setLoading(true);
                try {
                    await dispatch(approveOrthodonticPlan(planData.id));
                    await loadOrthodonticPlan(type); // refresh lại data
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleUnapprove = async () => {
        if (!planData?.id) return;

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn hủy approve phác đồ chỉnh nha?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setLoading(true);
                try {
                    await dispatch(unapproveOrthodonticPlan(planData.id));
                    await loadOrthodonticPlan(type); // refresh lại data
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    //console.log("Rendered with planData:", planData);

    return (
        <>
            <Tooltip title={showComment ? "Đóng comment" : "Mở comment"} placement="left">
                <Button
                    type="primary"
                    shape="circle"
                    icon={showComment ? <CloseOutlined /> : <MessageOutlined />}
                    onClick={() => setShowComment(!showComment)}
                    style={{
                        position: "fixed",
                        right: 16,
                        top: "40%",
                        zIndex: 99,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                    }}
                />
            </Tooltip>

            <Row gutter={[16, 16]} className="mt-3 mb-3">
                {/* ẢNH TRƯỚC ĐIỀU TRỊ */}
                <Col span={8}>
                    <Divider orientation="left" orientationMargin={0}>Ảnh trước điều trị</Divider>
                    {planData?.anh_truoc_dieu_tri?.length > 0 ? (
                        <Image.PreviewGroup>
                            <Space size={[8, 8]} wrap>
                                {planData.anh_truoc_dieu_tri.map((img, index) => (
                                    <Image
                                        key={index}
                                        width={50}
                                        height={50}
                                        src={img.file_url_thumb}
                                        preview={{
                                            src: img.file_url_original,  // khi phóng to, mở ảnh gốc
                                        }}
                                        alt={`Ảnh trước điều trị ${index + 1}`}
                                        style={{
                                            objectFit: "cover",
                                            borderRadius: 6,
                                            border: "1px solid #f0f0f0"
                                        }}
                                        fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='50' height='50'><rect width='50' height='50' fill='#fafafa'/></svg>"
                                    />
                                ))}
                            </Space>
                        </Image.PreviewGroup>
                    ) : (
                        <Text type="secondary">Chưa có ảnh trước điều trị</Text>
                    )}
                </Col>

                {/* PHIM X-QUANG TRƯỚC ĐIỀU TRỊ */}
                <Col span={8}>
                    <Divider orientation="left" orientationMargin={0}>Phim X-quang trước điều trị</Divider>
                    {planData?.x_quang?.length > 0 ? (
                        <Image.PreviewGroup>
                            <Space size={[8, 8]} wrap>
                                {planData.x_quang.map((img, index) => (
                                    <Image
                                        key={index}
                                        width={50}
                                        height={50}
                                        src={img.file_url_thumb}
                                        preview={{
                                            src: img.file_url_original,  // khi phóng to, mở ảnh gốc
                                        }}
                                        alt={`Phim X-quang ${index + 1}`}
                                        style={{
                                            objectFit: "cover",
                                            borderRadius: 6,
                                            border: "1px solid #f0f0f0"
                                        }}
                                        fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='50' height='50'><rect width='50' height='50' fill='#fafafa'/></svg>"
                                    />
                                ))}
                            </Space>
                        </Image.PreviewGroup>
                    ) : (
                        <Text type="secondary">Chưa có phim X-quang</Text>
                    )}
                </Col>

                {/* ẢNH MẪU HÀM TRƯỚC ĐIỀU TRỊ */}
                <Col span={8}>
                    <Divider orientation="left" orientationMargin={0}>Ảnh mẫu hàm trước điều trị</Divider>
                    {planData?.anh_mau_ham_truoc_dieu_tri?.length > 0 ? (
                        <Image.PreviewGroup>
                            <Space size={[8, 8]} wrap>
                                {planData.anh_mau_ham_truoc_dieu_tri.map((img, index) => (
                                    <Image
                                        key={index}
                                        width={50}
                                        height={50}
                                        src={img.file_url_thumb}
                                        preview={{
                                            src: img.file_url_original,  // khi phóng to, mở ảnh gốc
                                        }}
                                        alt={`Ảnh mẫu hàm ${index + 1}`}
                                        style={{
                                            objectFit: "cover",
                                            borderRadius: 6,
                                            border: "1px solid #f0f0f0"
                                        }}
                                        fallback="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='50' height='50'><rect width='50' height='50' fill='#fafafa'/></svg>"
                                    />
                                ))}
                            </Space>
                        </Image.PreviewGroup>
                    ) : (
                        <Text type="secondary">Chưa có ảnh mẫu hàm</Text>
                    )}
                </Col>
            </Row>
            <Divider />
            <Row gutter={8}>
                <Col span={24}>
                    <Title level={5} style={{ marginBottom: 30, marginTop: 30 }}>
                        BỆNH ÁN CHỈNH NHA
                    </Title>
                </Col>
                <Col span={6}>
                    <Form.Item
                        label="Lần lập phác đồ"
                        name="ortho_plan_type"
                    >
                        <BaseSelect
                            options={orthodonticPlanTypes}
                            allowClear={false}
                            placeholder="Chọn lần lập phác đồ"
                            className="w-100"
                            defaultValue={type}
                            onChange={(value) => handleChangeType(value)}
                        />
                    </Form.Item>
                </Col>
                <Col span={18}>
                    <div style={{
                        textAlign: "right",
                    }}>
                        <Space>
                            {isApproved ? (
                                <div>
                                    <Button
                                        htmlType="button"
                                        icon={<CloseOutlined />}
                                        onClick={handleUnapprove}
                                        danger
                                        disabled={!allowUnapproved}
                                    >
                                        Hủy approve phác đồ chỉnh nha
                                    </Button>
                                    <div className="mt-2" style={{ color: 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
                                        Xác nhận bởi: {planData?.approved_by} -{" "}
                                        {isValidDate(planData?.approved_at, true)}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Button
                                        type="primary"
                                        style={{ background: "#52c41a", borderColor: "#52c41a" }}
                                        icon={<CheckCircleOutlined />}
                                        onClick={handleApprove}
                                        disabled={!allowApproved}
                                    >
                                        Approve phác đồ chỉnh nha
                                    </Button>
                                </>
                            )}
                            <PrintButton printText="In phác đồ chỉnh nha">
                                <PrintOrthoPlan
                                    customer={customer}
                                    data={planData}
                                />
                            </PrintButton>
                        </Space>
                    </div>
                </Col>
            </Row>

            <Row gutter={8}>
                <Col span={showComment ? 18 : 24}>
                    <Spin spinning={loading}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            onFinishFailed={onFinishFailed}
                            disabled={isApproved}
                            className="form-readonly-custom-bg"
                            initialValues={defaultValues}
                        >
                            <Row gutter={8} className="mt-3 mb-3">
                                <Col span={12}>
                                    <Form.Item
                                        label="Bác sĩ phụ trách ca"
                                        name="plan_doctor_ids"
                                        rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                    >
                                        <BaseSelect
                                            mode="multiple"
                                            options={doctors || []}
                                            optionValue="user_id"
                                            optionLabel="display_name"
                                            showSearch
                                            placeholder="Bác sĩ thực hiện"
                                            className="w-100"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Bác sĩ lập phác đồ, thực hiện thủ thuật"
                                        name="plan_creator_ids"
                                        rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                    >
                                        <BaseSelect
                                            mode="multiple"
                                            options={doctors || []}
                                            optionValue="user_id"
                                            optionLabel="display_name"
                                            showSearch
                                            placeholder="Bác sĩ lập phác đồ"
                                            className="w-100"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        label="Mức độ khó của ca"
                                        name="muc_do_kho_ca"
                                        rules={[{ required: false, message: "Trường bắt buộc!" }]}
                                    >
                                        <BaseSelect
                                            options={[
                                                { value: '1', label: 'Mức 1' },
                                                { value: '2', label: 'Mức 2' },
                                                { value: '3', label: 'Mức 3' },
                                                { value: '4', label: 'Mức 4' },
                                            ]}
                                            showSearch
                                            placeholder="Mức độ khó của ca"
                                            className="w-100"
                                        />
                                    </Form.Item>
                                </Col>

                            </Row>

                            <Text strong>Phần 1: Chẩn đoán</Text>
                            <DiagnosticSection toothData={toothData} onToothDataChange={handleToothDataChange} />

                            {/* --- PHẦN 2: MỤC TIÊU --- */}
                            <Divider orientation="left" orientationMargin={0} className="mt-4">Phần 2: Mục tiêu điều trị</Divider>

                            <Form.List
                                name="treatment_goals"
                            >
                                {(fields, { add, remove }) => (
                                    <>
                                        <Table
                                            dataSource={fields}
                                            columns={[
                                                {
                                                    title: "Vấn đề cần khắc phục",
                                                    dataIndex: "issue",
                                                    width: '49%',
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "issue"]}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <TextArea rows={3} placeholder={`Vấn đề ${index + 1}`} />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Ghi chú",
                                                    dataIndex: "note",
                                                    width: '49%',
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "note"]}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <TextArea
                                                                rows={3}
                                                                placeholder="Giải quyết / cải thiện / chấp nhận"
                                                            />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "#",
                                                    dataIndex: "actions",
                                                    width: '2%',
                                                    align: "center",
                                                    render: (_, __, index) => (
                                                        <Popconfirm
                                                            title="Xóa dòng này?"
                                                            onConfirm={() => remove(index)}
                                                            okText="Xóa"
                                                            cancelText="Hủy"
                                                            placement="left"
                                                        >
                                                            <DeleteOutlined
                                                                style={{ color: "red", fontSize: "16px", cursor: "pointer" }}
                                                            />
                                                        </Popconfirm>
                                                    ),
                                                },
                                            ]}
                                            rowKey={(record, index) => index}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            locale={{ emptyText: "Chưa có dữ liệu" }}
                                        />

                                        <Row justify="center">
                                            <Button
                                                type="primary"
                                                size="middle"
                                                block
                                                className="mt-3 w-25"
                                                onClick={() => add()}
                                                icon={<PlusOutlined />}
                                            >
                                                Thêm dòng mới
                                            </Button>
                                        </Row>
                                    </>
                                )}
                            </Form.List>

                            {/* --- PHẦN 3: PHÁC ĐỒ ĐỀ NGHỊ --- */}
                            <Divider orientation="left" orientationMargin={0}>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    Phần 3: Phác đồ điều trị chỉnh nha đề nghị
                                    <Tooltip
                                        title={
                                            <div style={{ whiteSpace: "pre-line", margin: 0, color: "#000" }}>
                                                {tooltipTitle}
                                            </div>
                                        }
                                        placement="right"
                                        overlayInnerStyle={{ backgroundColor: "#fff", color: "#000" }}
                                        overlayStyle={{ maxWidth: 600 }}
                                    >
                                        <QuestionCircleOutlined style={{ color: "#1890ff", cursor: "pointer" }} />
                                    </Tooltip>

                                </span>
                            </Divider>

                            <Form.Item
                                name="phac_do_dieu_tri_de_nghi"
                                extra="Phác đồ sẽ bị khóa sau khi được approve"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                            >
                                <TextArea
                                    rows={16}
                                    placeholder={tooltipTitle}
                                />
                            </Form.Item>

                            {/* --- PHẦN 4: CÁC GIAI ĐOẠN DỰ KIẾN --- */}
                            <Divider orientation="left" orientationMargin={0}>Phần 4: Các giai đoạn điều trị dự kiến</Divider>
                            <Form.List name="treatment_stages">
                                {(fields, { add, remove }) => (
                                    <>
                                        <Table
                                            dataSource={fields}
                                            columns={[
                                                {
                                                    title: "Giai đoạn",
                                                    dataIndex: "giai_doan",
                                                    width: "15%",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "giai_doan"]}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <TextArea rows={3} placeholder="Nhập giai đoạn" />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Mục tiêu",
                                                    dataIndex: "muc_tieu",
                                                    width: "30%",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "muc_tieu"]}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <TextArea rows={3} placeholder="Nhập mục tiêu" />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Phương pháp",
                                                    dataIndex: "phuong_phap",
                                                    width: "30%",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "phuong_phap"]}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <TextArea rows={3} placeholder="Nhập phương pháp" />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Thời gian",
                                                    dataIndex: "thoi_gian",
                                                    width: "30%",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "thoi_gian"]}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                            style={{ marginBottom: 0 }}
                                                        >
                                                            <TextArea rows={3} placeholder="Nhập thời gian" />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "#",
                                                    dataIndex: "actions",
                                                    width: '2%',
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Popconfirm
                                                            title="Xóa dòng này?"
                                                            okText="Xóa"
                                                            cancelText="Hủy"
                                                            placement="left"
                                                            onConfirm={() => remove(index)}
                                                        >
                                                            <DeleteOutlined
                                                                style={{ color: "red", fontSize: "16px", cursor: "pointer" }}
                                                            />
                                                        </Popconfirm>
                                                    ),
                                                },
                                            ]}
                                            rowKey={(record, index) => index}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            locale={{ emptyText: "Chưa có dữ liệu" }}
                                        />

                                        <Row justify="center">
                                            <Button
                                                type="primary"
                                                size="middle"
                                                block
                                                className="mt-3 w-25"
                                                onClick={() => add()}
                                                icon={<PlusOutlined />}
                                            >
                                                Thêm dòng mới
                                            </Button>
                                        </Row>
                                    </>
                                )}
                            </Form.List>

                            {/* --- PHẦN 5: DIỄN TIẾN --- */}
                            <Divider orientation="left" orientationMargin={0}>Phần 5: Diễn tiến điều trị</Divider>
                            <Form.List name="progress">
                                {(fields, { add, remove }) => (
                                    <>
                                        <Table
                                            dataSource={fields}
                                            columns={[
                                                {
                                                    title: "Lần TK",
                                                    dataIndex: "lan_tk",
                                                    width: "8%",
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "lan_tk"]}
                                                            style={{ marginBottom: 0 }}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                        >
                                                            <TextArea rows={4} placeholder="Lần TK" />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Ngày TK",
                                                    dataIndex: "ngay_tk",
                                                    width: "12%",
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "ngay_tk"]}
                                                            style={{ marginBottom: 0 }}
                                                            rules={[{ required: false, message: "Bắt buộc nhập ngày TK" }]}
                                                        >
                                                            <DatePicker
                                                                format="DD-MM-YYYY"
                                                                style={{ width: "100%" }}
                                                                placeholder="Chọn ngày"
                                                            />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "BS/Y tá thực hiện thủ thuật",
                                                    dataIndex: "nguoi_thuc_hien",
                                                    width: "15%",
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "nguoi_thuc_hien"]}
                                                            style={{ marginBottom: 0 }}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                        >
                                                            <TextArea rows={4} />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Vấn đề / Mục tiêu điều trị",
                                                    dataIndex: "van_de_muc_tieu",
                                                    width: "15%",
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "van_de_muc_tieu"]}
                                                            style={{ marginBottom: 0 }}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                        >
                                                            <TextArea rows={4} />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Giải pháp & chỉ định",
                                                    dataIndex: "giai_phap_chi_dinh",
                                                    width: "15%",
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "giai_phap_chi_dinh"]}
                                                            style={{ marginBottom: 0 }}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                        >
                                                            <TextArea rows={4} />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Nội dung cuộc hẹn lần tới",
                                                    dataIndex: "noi_dung_hen_toi",
                                                    width: "15%",
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "noi_dung_hen_toi"]}
                                                            style={{ marginBottom: 0 }}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                        >
                                                            <TextArea rows={4} />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "Ý kiến BS PTCM/CVCM",
                                                    dataIndex: "y_kien_bs_ptcm_cvcm",
                                                    width: "15%",
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Form.Item
                                                            name={[index, "y_kien_bs_ptcm_cvcm"]}
                                                            style={{ marginBottom: 0 }}
                                                            rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                                        >
                                                            <TextArea rows={4} />
                                                        </Form.Item>
                                                    ),
                                                },
                                                {
                                                    title: "#",
                                                    dataIndex: "actions",
                                                    width: '2%',
                                                    align: "center",
                                                    render: (_, record, index) => (
                                                        <Popconfirm
                                                            title="Xóa dòng này?"
                                                            okText="Xóa"
                                                            cancelText="Hủy"
                                                            placement="left"
                                                            onConfirm={() => remove(index)}
                                                        >
                                                            <DeleteOutlined
                                                                style={{ color: "red", fontSize: "16px", cursor: "pointer" }}
                                                            />
                                                        </Popconfirm>
                                                    ),
                                                },
                                            ]}
                                            rowKey={(record, index) => index}
                                            pagination={false}
                                            size="small"
                                            bordered
                                            locale={{ emptyText: "Chưa có dữ liệu" }}
                                        />

                                        <Row justify="center">
                                            <Button
                                                type="primary"
                                                size="middle"
                                                block
                                                className="mt-3 w-25"
                                                onClick={() => add()}
                                                icon={<PlusOutlined />}
                                            >
                                                Thêm dòng mới
                                            </Button>
                                        </Row>
                                    </>
                                )}
                            </Form.List>

                            <div style={{
                                // marginTop: "30px",
                                // position: "sticky",
                                bottom: 0,
                                background: "#fff",
                                padding: "12px 0",
                                textAlign: "right",
                                zIndex: 100,
                                borderTop: "1px solid #f0f0f0"
                            }}>
                                {allowUpdate &&
                                    <Space>
                                        <Button
                                            htmlType="button"
                                            size="large"
                                            onClick={() => form.resetFields()}
                                            icon={<ReloadOutlined />}
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="large"
                                            htmlType="submit"
                                            loading={loading}
                                            icon={<SaveOutlined />}
                                        >
                                            Lưu
                                        </Button>

                                    </Space>
                                }
                            </div>
                        </Form>
                    </Spin>
                </Col>
                {showComment && (
                    <Col span={6}>
                        <CommentForm planId={planData?.id} />
                    </Col>
                )}
            </Row>
        </>
    );
};

export default Index;
